/** @forge-header
 * @id          workingCloses
 * @role        Resting orders that will close a Kalshi position when they fill
 * @pattern     pure
 * @purity      pure
 * @units       contracts (fractional allowed — fp fills)
 * @money-path  yes
 * @upstream    trading/kalshiCancelLifecycle
 * @downstream  views/PositionTracker/index | views/PositionTracker/EditPositionModal
 * @exports     workingCloses:fn | WorkingCloses:type
 * @test        workingCloses.test.ts
 */

import type { KalshiOrder } from '../../types/kalshi';
import type { KalshiCancelStates } from './kalshiCancelLifecycle';

type RestingOrder = Pick<KalshiOrder, 'orderId' | 'ticker' | 'side' | 'action' | 'remainingCount'>;

export interface WorkingCloses<O extends RestingOrder = RestingOrder> {
  /** Contracts on every resting order that nets this position down on fill. */
  qty: number;
  orders: O[];
}

/**
 * Every resting order on the position's ticker that reduces it when it fills:
 * a SELL of the held side, or a BUY of the other side (#949 — Kalshi nets YES
 * and NO on one ticker into a single signed position).
 *
 * The two kinds are deliberately ONE count. On Kalshi's single book a sell YES
 * and a buy NO are the same ask (and sell NO / buy YES the same bid), so once
 * an order reads back (`reconstructOrderTraderSide`) a trader's own resting
 * sell-to-close is indistinguishable from an opposite-side buy. Only the
 * optimistic pre-poll insert still says "sell". Splitting them would show a
 * label the venue can't back.
 *
 * Excluded: orders whose cancel the venue already accepted (awaiting removal
 * from the lagging list, or timed out waiting) — they can no longer fill — and
 * zero-remaining leftovers from fractional rounding. Kalshi only: Polymarket
 * YES/NO are separate tokens and do not net.
 */
export function workingCloses<O extends RestingOrder>(
  pos: { ticker: string; side: 'yes' | 'no' },
  openOrders: readonly O[],
  cancelStates: KalshiCancelStates = {},
): WorkingCloses<O> {
  let qty = 0;
  const orders: O[] = [];
  for (const o of openOrders) {
    if (o.ticker !== pos.ticker || !(o.remainingCount > 0)) continue;
    const closes =
      (o.side === pos.side && o.action === 'sell') || (o.side !== pos.side && o.action === 'buy');
    if (!closes) continue;
    const phase = cancelStates[o.orderId]?.phase;
    if (phase === 'awaiting-removal' || phase === 'timed-out') continue;
    qty += o.remainingCount;
    orders.push(o);
  }
  return { qty, orders };
}
